import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Cloud, CloudFog, CloudLightning, CloudRain, CloudSun, MapPin, Search, Sun } from 'lucide-react';
import { api, errorMessage, qs } from '@/lib/api';
import { formatDateVi } from '@/lib/format';
import { Alert, Spinner } from '@/components/ui/feedback';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface SurveyLocation {
  name: string;
  latitude: number;
  longitude: number;
}

interface GeocodedLocation extends SurveyLocation {
  id: number;
  admin1: string | null;
  country: string;
}

export interface WeatherForecastData {
  date: string;
  locationName: string;
  available: boolean;
  fetchedAt?: string;
  condition?: string;
  weatherCode?: number;
  temperatureC?: number | null;
  precipitationProbabilityPct?: number | null;
  rainfallMm?: number | null;
  windSpeedKmh?: number | null;
  message?: string;
}

export function ForecastCards({
  forecasts,
  locationName,
}: {
  forecasts: WeatherForecastData[];
  locationName: string;
}) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {forecasts.map((forecast) => {
        const WeatherIcon = weatherConditionIcon(forecast.weatherCode);
        return (
          <article
            key={forecast.date}
            className="min-w-0 rounded-lg border border-slate-200 bg-white px-2 py-2"
          >
            <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {formatDateVi(forecast.date)}
            </p>
            {forecast.available ? (
              <div className="mt-1 flex min-w-0 items-center gap-1.5">
                <WeatherIcon
                  className="size-5 shrink-0 text-sky-600"
                  aria-label={forecast.condition}
                  role="img"
                />
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-medium text-slate-700" title={forecast.condition}>
                    {forecast.condition}
                  </p>
                  <p className="text-xs font-semibold text-slate-900">
                    {forecast.temperatureC == null ? '—' : `${forecast.temperatureC.toFixed(1)} °C`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-500">
                <WeatherIcon className="size-5 shrink-0 text-slate-400" aria-hidden="true" />
                <span className="truncate" title={forecast.message ?? 'Chưa có dự báo'}>
                  Chưa có dự báo
                </span>
              </div>
            )}
          </article>
        );
      })}
      <p className="col-span-3 truncate text-[10px] text-slate-500" title={locationName}>
        Thông tin tham khảo · Nguồn:{' '}
        <a className="underline" href="https://open-meteo.com/" target="_blank" rel="noreferrer">
          Open-Meteo
        </a>{' '}
        · {locationName}
      </p>
    </div>
  );
}

export function weatherConditionIcon(code?: number) {
  if (code === 0) return Sun;
  if (code === 45 || code === 48) return CloudFog;
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code ?? -1)) return CloudRain;
  if ([95, 96, 99].includes(code ?? -1)) return CloudLightning;
  if ([1, 2].includes(code ?? -1)) return CloudSun;
  if (code === 3) return Cloud;
  return CloudSun;
}

function locationLabel(place: GeocodedLocation) {
  return [place.name, place.admin1, place.country].filter(Boolean).join(', ');
}

export function WeatherForecast({
  startDate,
  location,
  onLocationChange,
}: {
  startDate: string;
  location: SurveyLocation | null;
  onLocationChange: (location: SurveyLocation | null) => void;
}) {
  const [locationQuery, setLocationQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLocationSearchOpen, setIsLocationSearchOpen] = useState(false);
  const places = useQuery({
    queryKey: ['booking-weather-locations', searchQuery],
    queryFn: () =>
      api.get<GeocodedLocation[]>(`/booking/weather/locations${qs({ query: searchQuery })}`),
    enabled: searchQuery.length >= 2,
    staleTime: 10 * 60_000,
  });
  const forecast = useQuery({
    queryKey: ['booking-weather-forecast', startDate, location?.latitude, location?.longitude],
    queryFn: () =>
      api.get<{ locationName: string; forecasts: WeatherForecastData[] }>(
        `/booking/weather/forecast-range${qs({
          startDate,
          days: 3,
          locationName: location!.name,
          latitude: location!.latitude,
          longitude: location!.longitude,
        })}`,
      ),
    enabled: !!location,
    staleTime: 10 * 60_000,
  });
  useEffect(() => {
    setLocationQuery(location?.name ?? '');
  }, [location?.name]);

  return (
    <section className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-2.5 sm:p-3" aria-labelledby="weather-title">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <CloudSun className="size-4 shrink-0 text-sky-700" aria-hidden="true" />
          <h3 id="weather-title" className="text-sm font-semibold text-slate-800">
            Dự báo thời tiết
          </h3>
        </div>
        <button
          type="button"
          className="shrink-0 text-xs font-medium text-brand-700 hover:underline"
          aria-expanded={isLocationSearchOpen}
          aria-controls="weather-location-search"
          onClick={() => setIsLocationSearchOpen((open) => !open)}
        >
          {isLocationSearchOpen ? 'Thu gọn' : location ? 'Đổi địa điểm' : 'Chọn địa điểm'}
        </button>
      </div>
      {isLocationSearchOpen && (
        <div id="weather-location-search" className="space-y-2">
          <p className="text-[11px] text-slate-500">
            Tra cứu địa điểm khảo sát qua Open-Meteo.
          </p>
          <div className="flex gap-2">
            <Input
              aria-label="Địa điểm khảo sát"
              className="min-w-0"
              value={locationQuery}
              onChange={(event) => {
                setLocationQuery(event.target.value);
                setSearchQuery('');
                if (location) onLocationChange(null);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setSearchQuery(locationQuery.trim());
                }
              }}
              placeholder="VD: Đà Nẵng, Việt Nam"
              maxLength={100}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Tìm địa điểm"
              loading={places.isFetching}
              disabled={locationQuery.trim().length < 2}
              onClick={() => setSearchQuery(locationQuery.trim())}
            >
              <Search />
            </Button>
          </div>
          {places.error && (
            <Alert tone="warning">
              {errorMessage(places.error)}{' '}
              <button className="font-semibold underline" type="button" onClick={() => places.refetch()}>
                Thử lại
              </button>
            </Alert>
          )}
          {places.data && searchQuery && (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded border border-slate-200 bg-white p-1">
              {places.data.length ? (
                places.data.map((place) => (
                  <button
                    className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-sky-50 focus-visible:outline-2 focus-visible:outline-sky-700"
                    key={place.id}
                    type="button"
                    onClick={() => {
                      const name = locationLabel(place);
                      onLocationChange({
                        name,
                        latitude: place.latitude,
                        longitude: place.longitude,
                      });
                      setLocationQuery(name);
                      setSearchQuery('');
                    }}
                  >
                    <MapPin className="mt-0.5 size-4 shrink-0 text-slate-500" />
                    <span>{locationLabel(place)}</span>
                  </button>
                ))
              ) : (
                <p className="px-2 py-2 text-sm text-slate-500">Không tìm thấy địa điểm phù hợp.</p>
              )}
            </div>
          )}
          {location && (
            <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
              <span className="truncate">Địa điểm đã chọn: {location.name}</span>
              <button
                type="button"
                className="shrink-0 font-medium text-brand-700 hover:underline"
                onClick={() => {
                  onLocationChange(null);
                  setLocationQuery('');
                }}
              >
                Bỏ chọn
              </button>
            </div>
          )}
        </div>
      )}
      {forecast.isLoading && <Spinner />}
      {forecast.error && (
        <Alert tone="warning">
          Không thể tải dữ liệu thời tiết. Lịch khảo sát vẫn có thể được đặt.
          <button className="ml-1 font-semibold underline" type="button" onClick={() => forecast.refetch()}>
            Thử lại
          </button>
        </Alert>
      )}
      {forecast.data?.forecasts && forecast.data.forecasts.length > 0 && (
        <ForecastCards forecasts={forecast.data.forecasts} locationName={forecast.data.locationName} />
      )}
      {!location && !forecast.isLoading && (
        <p className="text-[11px] text-slate-500">Chọn địa điểm để xem dự báo thời tiết.</p>
      )}
    </section>
  );
}
